/**
 * 首页 Banner Tab：直接列轮播图。
 *
 * Banner = Home Discover，只有这一个位，不再先画一层位聚合表。
 * 标题列只取当前语言的确切 title_i18n 值（不回退 en/zh）；无标题帧（`{}`，dev-docs#1534）显示「—」。
 * 全局位按语言各一行（dev-docs `01-通用约定/85-运营配置维度模型.md` §5）：某语言还没有全局位时，
 * 「全部」视图空态给建位入口（POST /ops/banners，app_id=null）；App 视图的独立配置只能从该语言全局复制。
 * 帧表对照 ops-banner-slides.jsx；写动作复用既有 slides API。
 */

import type { QueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fromSlide, offerServingBadge } from '~/data/adapters/ops-adapter';
import type { Banner } from '~/data/mock/types';
import { useBanners, useSlides } from '~/data/queries';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ContextMenu } from '~/ui/ContextMenu';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { NewSlideDialog } from './dialogs/NewSlideDialog';
import type { Slide } from './dialogs/banner-slide-view-model';
import { exactI18nText } from './has-exact-i18n';
import { isOpsGlobal } from './ops-scope';
import { resolveWriteTarget } from './resolve-write-target';
import { type OpsMutations, errorToast, successToast } from './use-ops-mutations';

const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';
const GLOBAL_SLIDE_LOCKED_TITLE =
  '当前为 App 视图下的全局 Banner · slide 为全局共享，禁止在此改/删（会污染所有 App）· 请先创建该 App 的覆盖位';

const TARGET_LABEL: Record<string, string> = {
  book: 'book 书籍',
  offer: 'offer 活动',
  web: 'web 链接',
};

/** 只取当前层（全部=全局 / 某 App=该 App 行），不把全局帧掺进 App 视图。 */
export function pickHomeDiscoverBanner(banners: Banner[], appId: string): Banner | undefined {
  const inScope = isOpsGlobal(appId)
    ? banners.filter((b) => (b.appId ?? null) === null)
    : banners.filter((b) => b.appId === appId);
  return (
    inScope.find(
      (b) =>
        b.slug === 'discover' ||
        /home|discover/i.test(b.slug) ||
        /Home Discover|首页/i.test(b.position),
    ) ?? inScope[0]
  );
}

export function isAppDiscoverBanner(banner: Banner, appId: string): boolean {
  return banner.appId === appId && banner.slug === 'discover';
}

/** 「全部」层当前语言有确切标题的轮播条数，给从全局复制计数。 */
export function useBannerGlobalCount(lang: string): number {
  const { data: banners } = useBanners('all', lang);
  const banner = pickHomeDiscoverBanner(banners, 'all');
  const { data: slides } = useSlides(banner?.id ?? null, lang);
  return slides.length;
}

export function BannerSlidesTab({
  appId,
  lang,
  mutations,
  canUpsert,
  canDelete,
  queryClient,
  onConfirm,
}: {
  appId: string;
  lang: string;
  mutations: OpsMutations;
  canUpsert: boolean;
  canDelete: boolean;
  queryClient: QueryClient;
  onConfirm: (cfg: {
    title: string;
    message: string;
    tone?: 'danger' | 'warn' | 'default';
    confirmLabel?: string;
    onConfirm: () => void;
  }) => void;
}) {
  const {
    data: banners,
    isSuccess: bannersLoaded,
    isError: bannersFailed,
    configSource,
  } = useBanners(appId, lang);
  const banner = pickHomeDiscoverBanner(banners, appId);
  const bannerId = banner?.id ?? null;
  const { data: slideDtos } = useSlides(bannerId, lang);
  const allSlides: Slide[] = slideDtos.map(fromSlide);
  const slides: Slide[] = allSlides;
  const [editing, setEditing] = useState<Slide | null>(null);

  // 建该语言全局位时的 slug 参照：C 端按 slug 匹配渲染位置，各语言全局位必须同 slug 才落在同一个位上。
  // 英语全局位是既有基线；连它都没有（全局层全空）时用约定默认值（本 Tab 模型只有 Home Discover 一个位）。
  const {
    data: globalEnBanners,
    isSuccess: globalEnLoaded,
    isError: globalEnFailed,
  } = useBanners('all', 'en');
  const enGlobalBanner = pickHomeDiscoverBanner(globalEnBanners, 'all');

  const globalSlideLocked = !isOpsGlobal(appId) && (banner?.appId ?? null) === null;
  const writeLocked = !canUpsert || globalSlideLocked || mutations.updateSlide.isPending;

  const toggleBanner = () => {
    if (!banner) return;
    const { row, targetAppId, asOverride } = resolveWriteTarget(
      queryClient,
      ['banners', appId, lang],
      banner,
      appId,
    );
    const fail = (err: unknown) => errorToast(err, row.isActive ? '下架失败' : '上架失败');

    if (asOverride) {
      // App 视图下切全局位的上下架 = 建该 App 的 override 位。POST（§3.2）服务端一律定
      // is_active=false，所以「要上架」得补一次 PATCH —— 同 FAQ §9.2 的「先建后上架」两步，
      // 不能把 is_active 塞进 POST（strict schema 会 422，且服务端也不认这个字段）。
      mutations.createBanner.mutate(
        { app_id: targetAppId, language: lang, slug: row.slug, name: row.name },
        {
          onSuccess: (created) => {
            if (row.isActive) return; // 目标就是下架，POST 建出来已是 false
            mutations.updateBanner.mutate(
              { id: created.id, body: { is_active: true } },
              {
                onError: fail,
              },
            );
          },
          onError: fail,
        },
      );
      return;
    }
    // 既有位（§3.3 PATCH）：只翻 is_active，app_id / language / slug 不可改
    mutations.updateBanner.mutate(
      { id: row.id, body: { is_active: !row.isActive } },
      { onError: fail },
    );
  };

  /**
   * slide 的 PATCH 载荷（§4.3 任意子集）。旧实现是全字段 PUT，得把 title_i18n / image_r2_key /
   * target_* 原样回传 —— 那会在并发下把别人刚改的这些字段按本地快照恢复。PATCH 只带真正要改的键。
   */
  const slidePatch = (s: Slide, body: { display_order?: number; is_active?: boolean }) => ({
    bannerId: bannerId as string,
    slideId: s.id,
    body,
  });

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    const a = slides[i];
    const b = slides[j];
    if (!a || !b || !bannerId || writeLocked) return;
    mutations.updateSlide.mutate(slidePatch(a, { display_order: b.displayOrder ?? j }), {
      onError: (err) => errorToast(err, '调整排序失败'),
    });
    mutations.updateSlide.mutate(slidePatch(b, { display_order: a.displayOrder ?? i }), {
      onError: (err) => errorToast(err, '调整排序失败'),
    });
  };

  /**
   * 新建本作用域的 Banner 位（A2 §3.2，POST `app_id` = 目标层 + 本语言）。
   * 各语言各一行（85 §5「没有『语言为空的全局行』」），新开语言必须先建位才能配轮播图。
   *
   * **两层共用一个入口**：全局层直接建（`app_id=null`）；App 层建在**已有的空独立配置**上
   * （`app_id=<当前 App>`）—— §3.2 的 App 层写入前置正是「该 `(banner_slides, app_id, language)`
   * 已存在未软删身份行」，而那行恰是 §0.4 `init_mode='empty'` 直建出来的。没有身份行时服务端返
   * `OPS_APP_DEDICATED_CONFIG_NOT_FOUND`，故无独立配置的 App 视图不给本入口。
   *
   * `slug` 两层都取英语全局位的值：C 端按 slug 匹配渲染位置，App 层位换个 slug 就匹配不上。
   * 位建出来默认下架（服务端定 `is_active=false`），配完轮播图再上架。
   */
  const createPos = (targetAppId: string | null) => {
    if (!canUpsert || mutations.createBanner.isPending) return;
    mutations.createBanner.mutate(
      {
        app_id: targetAppId,
        language: lang,
        slug: enGlobalBanner?.slug ?? 'discover',
        name: enGlobalBanner?.name ?? 'Home Discover',
      },
      {
        onSuccess: () =>
          successToast(
            `已新建 ${lang.toUpperCase()} ${targetAppId === null ? '全局' : '本 App'} Banner 位 · 默认下架`,
          ),
        onError: (err) => errorToast(err, '新建 Banner 位失败'),
      },
    );
  };

  const openNew = () => {
    if (writeLocked || !bannerId) return;
    setEditing({
      id: '',
      target: '',
      to: '',
      hue: 250,
      titleI18n: { zh: '', en: '' },
      targetType: 'book',
      targetValue: '',
      imageR2Key: '',
      displayOrder: allSlides.reduce((max, s) => Math.max(max, (s.displayOrder ?? -1) + 1), 0),
      isActive: true,
    });
  };

  if (!banner) {
    const isGlobalScope = isOpsGlobal(appId);
    // App 视图下身份行仍在、内容行为空 = 「已有独立配置且为空」（85 §3.1）：C 端读这层空覆盖、
    // **不回退全局**。它既是内容行被删空的结果，也是 §0.4 `init_mode='empty'` 直建的落地态 ——
    // 后者正是「全局层为空即无法建覆盖」那条死结的解法（85 §3.1），运营在这个态上要能继续配。
    const emptyDedicated = !isGlobalScope && configSource === 'APP_DEDICATED';
    // 本作用域能否建位：全局层直接建；App 层只在**已有独立配置**时建（§3.2 的写入前置就是那行
    // 身份行）。无独立配置的 App 视图不给入口 —— 那一步该先建独立配置（复制或直建）。
    const canCreatePos = isGlobalScope || emptyDedicated;
    // 本作用域的三态判定：`useBanners` 在 pending / 断网 / 失败时同样返回空数组，「没有位」在
    // 查询落地前是未知而非事实 —— 提前放行会拿兜底 slug 建出与英语位对不上的重复位。
    // 英语全局查询只服务于建位流程，故只在能建位的作用域纳入判定（否则它慢 / 失败会让
    // 不建位的 App 视图误报加载中 / 失败）。
    const scopeResolved = bannersLoaded && (!canCreatePos || globalEnLoaded);
    const scopeFailed = bannersFailed || (canCreatePos && globalEnFailed);
    return (
      <div className="card" style={{ padding: '34px 18px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>暂无 Banner</div>
        <div
          style={{
            fontSize: 11.5,
            color: 'var(--fg-muted)',
            marginTop: 6,
            lineHeight: 1.6,
          }}
        >
          {scopeFailed
            ? 'Banner 位读取失败 · 刷新页面重试；未读到既有位前不能建位（会建出与英语位对不上的重复位）'
            : !scopeResolved
              ? '正在读取 Banner 位…'
              : isGlobalScope
                ? `${lang.toUpperCase()} ${langName(lang)} 还没有全局 Banner 位 · 各语言各一行，不回退英文`
                : emptyDedicated
                  ? '该 App 该语言的独立配置当前为空 · C 端在该 App 上不展示 Banner，不回退全局；可在此建本 App 的位，或移除独立配置恢复继承'
                  : '该 App 该语言无独立配置 · C 端读该语言全局位；要单独配置请先从该语言全局复制或直建'}
        </div>
        {canCreatePos && scopeResolved && (
          <button
            type="button"
            className="btn btn-sm btn-accent"
            style={{ marginTop: 14 }}
            disabled={!canUpsert || mutations.createBanner.isPending}
            title={canUpsert ? undefined : FORBIDDEN_ROLE_TITLE}
            onClick={() =>
              onConfirm({
                title: `新建 ${lang.toUpperCase()} ${isGlobalScope ? '全局' : '本 App'} Banner 位？`,
                message: `位 slug=${enGlobalBanner?.slug ?? 'discover'}（与英语全局位一致，C 端按 slug 匹配渲染位置）· 创建后默认下架，配好轮播图再上架。`,
                confirmLabel: '新建',
                onConfirm: () => createPos(isGlobalScope ? null : appId),
              })
            }
          >
            <Icons.IconPlus size={11} />
            新建 {lang.toUpperCase()} {isGlobalScope ? '全局位' : '本 App 位'}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <Section
        pad="4px 18px 16px"
        title={`Banner · ${lang.toUpperCase()} ${langName(lang)}`}
        sub={
          <>
            <span>{banner.position}</span>
            {' · '}
            <span className="mono">slug={banner.slug}</span>
            {' · 轮播图即 Banner，只有这一个位'}
          </>
        }
        action={
          <div className="hstack" style={{ gap: 6 }}>
            <span
              className={`chip ${banner.status === '上架' ? 'chip-pos' : 'chip-warn'}`}
              style={{ fontSize: 10 }}
            >
              {banner.status}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              disabled={writeLocked}
              title={
                globalSlideLocked
                  ? GLOBAL_SLIDE_LOCKED_TITLE
                  : !canUpsert
                    ? FORBIDDEN_ROLE_TITLE
                    : undefined
              }
              onClick={openNew}
            >
              <Icons.IconPlus size={11} />
              新增轮播图
            </button>
            <ContextMenu
              items={[
                {
                  label: banner.status === '上架' ? '下架该位' : '上架该位',
                  icon: banner.status === '上架' ? 'IconPause' : 'IconPlay',
                  disabled: !canUpsert,
                  onClick: () =>
                    onConfirm({
                      title: `${banner.status === '上架' ? '下架' : '上架'}「${banner.position}」？`,
                      message:
                        banner.status === '上架'
                          ? '下架后客户端不再展示任何轮播图。'
                          : '上架后客户端立即展示下发中的轮播图。',
                      tone: banner.status === '上架' ? 'warn' : 'default',
                      confirmLabel: '确认',
                      onConfirm: toggleBanner,
                    }),
                },
              ]}
            >
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                style={{ width: 22, height: 22 }}
                title="更多"
              >
                <Icons.IconMoreV size={12} />
              </button>
            </ContextMenu>
          </div>
        }
      >
        {globalSlideLocked && (
          <div
            className="card-flat"
            style={{
              margin: '10px 0',
              padding: '10px 12px',
              background: 'var(--warn-soft)',
              borderColor: 'transparent',
              fontSize: 11.5,
              color: 'var(--warn)',
              lineHeight: 1.5,
            }}
          >
            该 Banner 为全局共享，轮播图改动会影响所有 App。请先创建本 App 覆盖后再维护。
          </div>
        )}
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 50 }}>排序</th>
              <th>标题（{lang.toUpperCase()}）</th>
              <th style={{ width: 96 }}>跳转</th>
              <th style={{ width: 170 }}>目标</th>
              <th style={{ width: 140 }}>serving</th>
              <th style={{ width: 72 }}>上下架</th>
              <th style={{ width: 92 }} />
            </tr>
          </thead>
          <tbody>
            {slides.map((s, i) => {
              const title = exactI18nText(s.titleI18n, lang);
              const sv = s.offerServingState
                ? offerServingBadge(s.offerServingState, {
                    slideActive: s.status === '上架',
                    bannerActive: banner.isActive,
                    bannerIsGlobal: (banner.appId ?? null) === null,
                  })
                : null;
              const servingLabel =
                s.status !== '上架'
                  ? '未上架'
                  : sv
                    ? sv.label === '展示中'
                      ? '下发中'
                      : sv.label
                    : '下发中';
              const servingChip =
                s.status !== '上架' ? '' : sv && sv.label !== '展示中' ? sv.chip : 'chip-pos';
              return (
                <tr key={s.id}>
                  <td className="mono muted">
                    {String((s.displayOrder ?? i) + 1).padStart(2, '0')}
                  </td>
                  <td style={{ fontSize: 12 }}>{title || '—'}</td>
                  <td>
                    <span className="chip mono" style={{ fontSize: 9.5 }}>
                      {TARGET_LABEL[s.targetType ?? ''] ?? s.target}
                    </span>
                  </td>
                  <td
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      maxWidth: 170,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={s.to}
                  >
                    {s.to || '—'}
                  </td>
                  <td>
                    <span
                      className={`chip ${servingChip}`}
                      style={{ fontSize: 10 }}
                      title={sv?.hint}
                    >
                      {servingLabel}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`chip ${s.status === '上架' ? 'chip-pos' : 'chip-warn'}`}
                      style={{ fontSize: 10 }}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td>
                    <div className="hstack" style={{ gap: 2 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                        disabled={i === 0 || writeLocked}
                        title="上移"
                        onClick={() => move(i, -1)}
                      >
                        <Icons.IconChevUp size={11} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                        disabled={i === slides.length - 1 || writeLocked}
                        title="下移"
                        onClick={() => move(i, 1)}
                      >
                        <Icons.IconChevDown size={11} />
                      </button>
                      <ContextMenu
                        items={[
                          {
                            label: '编辑该帧',
                            icon: 'IconEdit',
                            disabled: writeLocked,
                            onClick: () => setEditing(s),
                          },
                          {
                            label: s.status === '上架' ? '下架该帧' : '上架该帧',
                            icon: s.status === '上架' ? 'IconPause' : 'IconPlay',
                            disabled: writeLocked,
                            onClick: () =>
                              onConfirm({
                                title: `${s.status === '上架' ? '下架' : '上架'}该轮播图？`,
                                message:
                                  s.status === '上架'
                                    ? '下架后客户端不再展示这一帧。'
                                    : '上架后按排序出现在客户端 Banner。',
                                tone: s.status === '上架' ? 'warn' : 'default',
                                confirmLabel: '确认',
                                onConfirm: () =>
                                  mutations.updateSlide.mutate(
                                    slidePatch(s, { is_active: !(s.isActive ?? true) }),
                                    {
                                      onError: (err) =>
                                        errorToast(err, s.isActive ? '下架失败' : '上架失败'),
                                    },
                                  ),
                              }),
                          },
                          '---',
                          {
                            label: '删除该帧',
                            icon: 'IconTrash',
                            tone: 'danger',
                            disabled: !canDelete || globalSlideLocked,
                            onClick: () =>
                              onConfirm({
                                title: '删除该轮播图？',
                                message: '仅删除这一帧，不影响其它语言或其它配置。',
                                tone: 'danger',
                                confirmLabel: '确认删除',
                                onConfirm: () =>
                                  bannerId &&
                                  mutations.deleteSlide.mutate(
                                    { bannerId, slideId: s.id },
                                    { onError: (err) => errorToast(err, '删除轮播图失败') },
                                  ),
                              }),
                          },
                        ]}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ width: 22, height: 22 }}
                        >
                          <Icons.IconMoreV size={12} />
                        </button>
                      </ContextMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
            {slides.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: '26px 12px',
                    textAlign: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 11.5,
                  }}
                >
                  {lang.toUpperCase()} 暂无轮播图 · 点右上角「新增轮播图」
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
          serving 由读时派生：书行下架 / 活动未开始或已过期 → 客户端过滤该帧，不改上下架。
        </div>
      </Section>
      {editing && bannerId && (
        <NewSlideDialog
          bannerId={bannerId}
          bannerName={banner.position}
          bannerAppId={banner.appId ?? null}
          slide={editing}
          lang={lang}
          mutations={mutations}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
